'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConnectorStore } from '@/lib/connectors/store';
import type { ConnectorOperation, HttpMethod, UpdateOperationInput } from '@/lib/connectors/types';
import { ParameterBuilder } from './parameter-builder';
import { RequestBodyEditor } from './request-body-editor';
import { ResponseMapper } from './response-mapper';

interface OperationEditorProps {
  connectorId: string;
  operation: ConnectorOperation;
}

const HTTP_METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

export function OperationEditor({ connectorId, operation }: OperationEditorProps) {
  const { updateOperation } = useConnectorStore();

  const [name, setName] = useState(operation.name);
  const [description, setDescription] = useState(operation.description || '');
  const [method, setMethod] = useState<HttpMethod>(operation.method);
  const [path, setPath] = useState(operation.path);

  // Reset form when operation changes
  useEffect(() => {
    setName(operation.name);
    setDescription(operation.description || '');
    setMethod(operation.method);
    setPath(operation.path);
  }, [operation.id]);

  const handleUpdate = async (updates: UpdateOperationInput) => {
    try {
      await updateOperation(connectorId, operation.id, updates);
    } catch (error) {
      console.error('Failed to update operation:', error);
    }
  };

  const showRequestBody = ['POST', 'PUT', 'PATCH'].includes(method);

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Operation Details</CardTitle>
          <CardDescription>
            Configure the basic settings for this operation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="op-name">Operation Name</Label>
              <Input
                id="op-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleUpdate({ name })}
                placeholder="Get Users"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="method">HTTP Method</Label>
              <Select
                value={method}
                onValueChange={(value: HttpMethod) => {
                  setMethod(value);
                  handleUpdate({ method: value });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HTTP_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="path">Path</Label>
            <Input
              id="path"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              onBlur={() => handleUpdate({ path })}
              placeholder="/users/{user_id}"
              className="font-mono"
            />
            <p className="text-sm text-muted-foreground">
              Use {'{variable}'} syntax for path parameters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => handleUpdate({ description: description || undefined })}
              placeholder="Describe what this operation does..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Parameters & Request/Response */}
      <Tabs defaultValue="parameters">
        <TabsList>
          <TabsTrigger value="parameters">Parameters</TabsTrigger>
          {showRequestBody && (
            <TabsTrigger value="body">Request Body</TabsTrigger>
          )}
          <TabsTrigger value="response">Response</TabsTrigger>
        </TabsList>

        <TabsContent value="parameters" className="mt-4">
          <ParameterBuilder
            connectorId={connectorId}
            operation={operation}
          />
        </TabsContent>

        {showRequestBody && (
          <TabsContent value="body" className="mt-4">
            <RequestBodyEditor
              connectorId={connectorId}
              operation={operation}
            />
          </TabsContent>
        )}

        <TabsContent value="response" className="mt-4">
          <ResponseMapper
            connectorId={connectorId}
            operation={operation}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
